import { QueryClient } from '@tanstack/react-query'
import { fetchPage } from '@vangst/services/oogst/page'
import { ElasticSearchEndpoints } from '@vangst/services/oogst/search/useElasticSearch'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import Routes from '@vangst/services/routes'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import Busy from '../../components/feedback/Busy'
import Breadcrumbs from '../../components/navigation/Breadcrumbs'
import Meta from '../../domains/application/Meta'
import AttachmentMembers from '../../domains/recruiter/forms/AttachmentMembers'
import SearchVoltron from '../../domains/search/SearchVoltron'
import { parseParams } from '../../domains/search/useQueryParams'
import { getAddressFromVariables } from '../../domains/search/utils.server'

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const queryClient = new QueryClient()
  const page = await fetchPage(queryClient, { rawUrl: context.resolvedUrl })
  const qp = context.resolvedUrl.replace('/members?', '')
  const variables = { ...parseParams(qp), first: 24 }
  const address = await getAddressFromVariables(variables)

  const crumbs = [
    { href: Routes.INDEX, text: 'Home' },
    { href: null, text: 'Members' },
  ]

  return {
    props: {
      address,
      crumbs,
      page: page?.getPage ? page.getPage : null,
    },
  }
}

// -------------------------------------

type PropsType = InferGetServerSidePropsType<typeof getServerSideProps>

export default function MembersPage(props: PropsType) {
  const { crumbs, page } = props
  const { authenticated, authenticating, viewer } = useViewer()

  const { replace } = useRouter()
  useEffect(() => {
    if (!authenticated && !authenticating) replace(Routes.MEMBERS_FEATURED)
  }, [authenticated, authenticating, replace])

  const authorizedEndpoint: ElasticSearchEndpoints = viewer?.permissions
    .searchCandidatesAsRecruiter
    ? 'searchCandidatesByRecruiters'
    : viewer?.permissions.searchCandidatesAsClient
    ? 'searchCandidatesByClients'
    : 'searchCandidates'

  if (authenticating) {
    return (
      <header className="contain-xyc mood-white">
        <Busy />
      </header>
    )
  }

  if (authenticated) {
    return (
      <>
        <Meta
          key="meta"
          canonicalUrl={page?.metaInfo.canonicalUrl}
          crumbs={crumbs}
          description={page?.metaInfo.description}
          publishDate={page?.postedAt}
          robots={page?.metaInfo.robots}
          title={page?.metaInfo.title ?? page?.title ?? 'Vangst | Members'}
        />
        <Breadcrumbs crumbs={crumbs}>
          {viewer?.permissions?.uploadCsv && <AttachmentMembers />}
        </Breadcrumbs>
        <SearchVoltron
          address={props.address}
          endpoint={authorizedEndpoint}
          page={props.page}
        />
      </>
    )
  }
  return null
}
